import React, { useState, useEffect } from 'react';
import { Download, Check, ShoppingCart, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recursos = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingProduct, setProcessingProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (productId) => {
    setProcessingProduct(productId);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout/session`, {
        product_id: productId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Error al procesar el pago. Por favor, inténtalo de nuevo.');
      setProcessingProduct(null);
    }
  };

  if (loading) {
    return (
      <section id="recursos" className="bg-reset-dark py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white">Cargando recursos...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="recursos" className="bg-reset-dark py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-reset-gold uppercase tracking-widest text-sm mb-4 block">
            Tienda de recursos digitales
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-6">
            Recursos <span className="italic text-reset-gold">descargables</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Herramientas prácticas y sistemas listos para implementar IA en tu despacho hoy mismo. Descarga instantánea tras el pago.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {products.map((product) => (
            <div 
              key={product.id}
              className="border border-reset-gold/30 bg-zinc-900 p-6 rounded-lg hover:border-reset-gold transition-all relative"
            >
              {/* Bonus Badge */}
              {product.bonus && (
                <div className="absolute -top-3 -right-3 bg-reset-gold text-black px-4 py-2 rounded-full font-bold text-xs shadow-lg animate-pulse">
                  {product.bonus}
                </div>
              )}
              
              <div className="mb-4">
                <div className="text-4xl mb-3">{product.icon || '📦'}</div>
                <h3 className="text-white text-xl font-bold mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {product.description}
                </p>
              </div>

              <ul className="text-gray-200 text-xs space-y-2 mb-6">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className={feature.includes('🎁') ? 'text-reset-gold text-base' : 'text-reset-gold'}>
                      {feature.includes('🎁') ? '🎁' : '✓'}
                    </span>
                    <span className={feature.includes('🎁') ? 'font-semibold text-reset-gold' : ''}>
                      {feature.replace('🎁 ', '')}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex justify-between items-center border-t border-gray-800 pt-4">
                <div>
                  <span className="text-white text-2xl font-bold">
                    {product.price.toFixed(2)}€
                  </span>
                  <p className="text-gray-500 text-xs mt-1">{product.file_type}</p>
                  <p className="text-gray-600 text-xs">IVA incluido</p>
                </div>
                <button
                  onClick={() => handlePurchase(product.id)}
                  disabled={processingProduct === product.id}
                  data-testid={`buy-${product.id}`}
                  className="bg-reset-gold hover:bg-reset-gold-light text-black px-6 py-3 rounded font-bold text-sm transition-all inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingProduct === product.id ? (
                    <>
                      <Zap size={16} className="animate-pulse" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={16} />
                      <span>Descargar ahora</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-reset-black border border-reset-gold/20 p-6 text-center rounded-lg">
            <Zap className="text-reset-gold mx-auto mb-3" size={32} />
            <h4 className="text-white font-semibold mb-2">Descarga instantánea</h4>
            <p className="text-gray-400 text-sm">Acceso inmediato tras el pago</p>
          </div>
          <div className="bg-reset-black border border-reset-gold/20 p-6 text-center rounded-lg">
            <Check className="text-reset-gold mx-auto mb-3" size={32} />
            <h4 className="text-white font-semibold mb-2">Pago seguro</h4>
            <p className="text-gray-400 text-sm">Procesado por Stripe</p>
          </div>
          <div className="bg-reset-black border border-reset-gold/20 p-6 text-center rounded-lg">
            <Download className="text-reset-gold mx-auto mb-3" size={32} />
            <h4 className="text-white font-semibold mb-2">Uso ilimitado</h4>
            <p className="text-gray-400 text-sm">Descarga y usa sin restricciones</p>
          </div>
        </div>

        <div className="mt-16 bg-reset-black border border-reset-gold/20 p-8 rounded-lg">
          <h3 className="text-2xl font-semibold text-white mb-6">Preguntas frecuentes</h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-300">
            <div>
              <p className="font-semibold text-white mb-1">¿Cómo recibo el producto?</p>
              <p className="text-sm">Tras completar el pago, recibirás un email con el enlace de descarga instantáneo.</p>
            </div>
            <div>
              <p className="font-semibold text-white mb-1">¿Puedo usar el material en mi despacho?</p>
              <p className="text-sm">Sí, todos los recursos son de uso comercial para tu despacho.</p>
            </div>
            <div>
              <p className="font-semibold text-white mb-1">¿Hay garantía de devolución?</p>
              <p className="text-sm">Sí, garantía de 7 días si el producto no cumple tus expectativas.</p>
            </div>
            <div>
              <p className="font-semibold text-white mb-1">¿Los recursos se actualizan?</p>
              <p className="text-sm">Sí, algunos productos incluyen actualizaciones gratuitas durante 3 meses.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Recursos;